'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Space class
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//**** Space class
//***********************************************************************************
import { fh_clone, fh_matrix, fh_polygon, fh_scene, fh_solid, fh_sub } from '@acenv/fh-3d-viewer';
import { extension_instance } from '../extension/cn_extension';
import { CODES_BIM_OUVRAGES, CODES_BIM_PARAMETRES_OUVRAGES } from '../utils/cn_bbp_constants';
import { cn_bbp_geometry } from '../utils/cn_bbp_geometry';
import { logger } from '../utils/cn_logger';
import { generate_pattern_for_facing } from '../utils/cn_svg_patterns';
import { CN_CURRENT_DATE, cn_transaction_manager } from '../utils/cn_transaction_manager';
import { cn_add, cn_box, cn_clone, cn_color_hexa_to_rgb, cn_compare_polygons, cn_dist, cn_dot, cn_intersect_line, cn_manhattan_distance, cn_md5, cn_middle, cn_mul, cn_normal, cn_normalize, cn_sub, cn_uuid, cnx_add, cnx_clone, cnx_dot, cnx_mul, cnx_sub } from '../utils/cn_utilities';
import { cn_image_dir } from '../utils/image_dir';
import { cn_3d_building } from './cn_3d_building';
import { cn_area_context } from './cn_area_context';
import { cn_building } from './cn_building';
import { cn_contour } from './cn_contour';
import { cn_element } from './cn_element';
import { CN_FACING_TRIMMING_PLACEMENT_FLOOR } from './cn_facing_trimming';
import { cn_opening } from './cn_opening';
import { cn_scene } from './cn_scene';
import { cn_space_measure } from './cn_space_measure';
import { cn_storey } from './cn_storey';
import { cn_storey_element } from './cn_storey_element';
import { cn_zone } from './cn_zone';
import { cn_plugin_option } from '../utils/cn_plugin_option';
import { cn_space_metrics } from './cn_space_metrics';
import { cn_polygon_handler } from '../svg/cn_polygon_handler';
import { cn_space_segment } from './cn_space_segment';
import { CN_WALL_COSINE_SHAPE_THRESHOLD } from './cn_model_constants';
import { cn_camera } from '../svg/cn_camera';
import { cn_ceiling_type } from './cn_ceiling_type';
import { SPACE_EXTERIOR_LABEL } from './cn_model_constants';

/** Space numerotation */
class cn_space_numerotation {
    constructor(space_segment, text) {
        this.space_segment = space_segment;
        this.p0 = space_segment.points[0];
        this.p1 = space_segment.points[1];
        this.text = text;
        this.screen = [0,0];
    }

    draw(camera) {
        if (this.text == "") return "";
        let v0 = camera.world_to_screen(this.p0);
        let v1 = camera.world_to_screen(this.p1);
        let offset_direction = cn_normal(cn_sub(this.p0, this.p1));
        let o = [offset_direction[0], -offset_direction[1]];
        cn_normalize(o);
        const off = 30;
        o = cn_mul(o, off);
        v0 = cn_add(v0, o);
        v1 = cn_add(v1, o);
        return `<text text-anchor="middle" dominant-baseline="middle" class='dimensionning_text' x='${(v0[0] + v1[0]) * 0.5}' y='${(v0[1] + v1[1]) * 0.5}'>${this.text}</text>`;
    }

    draw_pastille(camera, mouseover) {
        if (this.text == "") return "";
        let v0 = camera.world_to_screen(this.p0);
        let v1 = camera.world_to_screen(this.p1);
        let offset_direction = cn_normal(cn_sub(this.p0, this.p1));
        let o = [offset_direction[0], -offset_direction[1]];
        cn_normalize(o);
        const off = 30;
        o = cn_mul(o, off);
        v0 = cn_add(v0, o);
        v1 = cn_add(v1, o);
        let vv = cn_middle(v0,v1);
        let html = "";
        let fill_color = (this.text == "A")?[255,130,0,1]:mouseover?[150,150,150,0.5]:[150,150,150,1];
        html += `<circle fill="rgba(${fill_color[0]},${fill_color[1]}, ${fill_color[2]},${fill_color[3]})" cx='${vv[0]}' cy='${vv[1]}' r="20" />`;
        html += `<text text-anchor="middle" dominant-baseline="middle" class='dimensionning_text' x='${vv[0]}' y='${vv[1]}'>${this.text}</text>`;
        this.screen = vv;
        return html;
    }

    under_point(mouse_screen) {
        return cn_dist(this.screen,mouse_screen) < 30;
    }
}

export class cn_space extends cn_element {
    /**
     * Construuctor
     * @param {cn_scene} scene
     */
    constructor(scene) {
        super(scene);
        this.class_name = 'cn_space';
        this.scene = scene;
        this.removable = false;

        //*** Model data
        this.name = '';
        this.index = -1;
        this.custom_index = -1;
        this.area = 0;
        this.plain_area = 0;
        this.perimeter = 0;
        this.outside = false;
        this.contours = [];
        this.diagonal_origin = 0;
        this.ceiling_height = 2.5;
        /** @type {cn_ceiling_type} */
        this.ceiling_type = null;
        this.regulated_ceiling = true;

        this.has_ceiling = true;
        this.slab_offset = 0;
        this.declared_area = -1;
        this.declared_perimeter = -1;

        this.declared_area_context = {};

        /** legacy, use space_usage from space_type instead */
        this.space_usage = '';
        this.space_type = null;
        this.space_type_index = 0;

        this.label_position = [0, 0];
        this.heated = true;
        /** index 0 = floor facing, index 1 = ceiling facing , index 2 : sub face*/
        this.facings = [null, null, null];
        this.substrates = [null, null, null];
        this.parent_space = null;

        /** @type {fh_polygon} */
        this.inner_polygon = new fh_polygon([0, 0, 0], [0, 0, 1]);

        /** @type {cn_space_segment[][]} */
        this.space_segments = [];

        //*** volatile data
        this.draw_priority = 0;
        this.center = [0, 0];
        this._center_date = -1;
        this.indoor = false;
        this.has_roof = true;
        this._area_for_label_position = -1;
        this.s_index = 0;

        this._indoor = {};
        this._has_roof = {};

        this._full_facing_floor_polygon = new fh_polygon([0, 0, 0], [0, 0, 1]);
        this._facing_floor_polygons = [];

        //*** polygons to draw
        this.polygons = [];

        //*** measure data */
        /** @type {cn_space_measure} */
        this.measure = new cn_space_measure(this);

        this._bounding_box = new cn_box();

        //*** Space label visibility: 0 = never, 1 = if large enough, 2 = always */
        this.space_label_visibility = cn_plugin_option.default_space_label_visibility;

        this._numerotation = [];
        this.unique_label = {};
        this.numerotation_space_segment = "";
    }

    static outside_space(parent) {
        let space = new cn_space(parent);
        let outside_space = parent.spaces? parent.spaces.find(sp => sp.outside) : null;
        if (outside_space)
            space.ID  = outside_space.ID;
        else
            space.ID = cn_uuid('outside_space' + parent.ID);
        space.outside = true;
        return space;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        var json = this.serialize_element();
        json.name = this.name;
        json.index = this.index;
        json.custom_index = this.custom_index;
        json.outside = this.outside;
        json.inner_polygon = this.inner_polygon.serialize();
        json.space_segments = this.space_segments.map(sslist => sslist.map(ss => ss.serialize()));
        json.measure = this.measure.serialize();
        json.ceiling_height = this.ceiling_height;
        json.ceiling_type = (this.ceiling_type) ? this.ceiling_type.ID : null;
        json.regulated_ceiling = this.regulated_ceiling;
        json.has_ceiling = this.has_ceiling;
        json.declared_area = this.declared_area;
        json.declared_perimeter = this.declared_perimeter;
        json.slab_offset = this.slab_offset;

        json.label_position = this.label_position;
        json.facings = this.facings.map((f) => (f ? { ID: f.ID } : null));
        json.substrates = this.substrates.map((f) => (f ? { ID: f.ID } : null));
        json.space_label_visibility = this.space_label_visibility;
        json.space_type = this.space_type ? this.space_type.ID : null;
        json.space_type_index = this.space_type_index;
        if (this.parent_space) json.parent_space = this.parent_space.ID;
        json.declared_area_context = this.declared_area_context;
        if (this.unique_label)
        {
            json.unique_label = {};
            for (var k in this.unique_label) {
                if (this.unique_label[k])
                    json.unique_label[k] = this.unique_label[k].ID;
            }
        }
        
        json.numerotation_space_segment = this.numerotation_space_segment;
        return json;
    }

    static unserialize(json, scene) {
        if (typeof (json.name) != 'string') return false;
        if (typeof (json.outside) != 'boolean') return false;


        var space = new cn_space(scene);
        space.unserialize_element(json);
        space.name = json.name;
        if (typeof (json.index) == 'number')
            space.index = json.index;
        if (typeof (json.custom_index) == 'number')
            space.custom_index = json.custom_index;
        space.outside = json.outside;

        if (json.inner_polygon)
            space.inner_polygon = fh_polygon.unserialize(json.inner_polygon);

        //*** Obsolete contour management */
        else if (json.contours) {
            space.inner_polygon = new fh_polygon([0, 0, 0], [0, 0, 1]);
            json.contours.forEach(contour => {
                if (contour.vertices) {
                    const ctr = contour.vertices.filter(v => v >= 0 && v < scene.vertices.length).map(v => cnx_clone(scene.vertices[v].position));
                    space.inner_polygon.add_contour(ctr);
                }
            });
            space.inner_polygon.compute_contours();
        }


        if (json.space_segments)
            space.space_segments = json.space_segments.map(sslist => sslist.map(ss => cn_space_segment.unserialize(ss, space)));
        space.space_usage = json.space_usage;
        if (typeof json.ID == 'string') space.ID = json.ID;

        if (typeof json.measure == 'object')
            cn_space_measure.unserialize(this, json.measure);

        if (typeof (json.ceiling_height) == 'number')
            space.ceiling_height = json.ceiling_height;

        if (typeof (json.regulated_ceiling) == 'boolean')
            space.regulated_ceiling = json.regulated_ceiling;

        let no_ceiling = false;
        if (typeof (json.ceiling_type) == 'string')
            space.ceiling_type = scene.building.get_ceiling_types().find(ct => ct.ID == json.ceiling_type);
        else if (typeof (json.ceiling_type) == 'undefined')
            no_ceiling = true;

        //*** backward compatibility for ceilings */
        if (space.ceiling_height < 0) {
            space.ceiling_height = 2.5;
            space.ceiling_type = null;
        }
        else if (no_ceiling) {
            const ceiling_types = scene.building.get_ceiling_types();
            if (ceiling_types.length)
                space.ceiling_type = ceiling_types[0];
        }
        if (space.outside) space.ceiling_type = null;

        if (typeof json.has_ceiling == 'boolean')
            space.has_ceiling = json.has_ceiling;

        if (typeof json.declared_area == 'number')
            space.declared_area = json.declared_area;

        if (typeof json.declared_perimeter == 'number')
            space.declared_perimeter = json.declared_perimeter;

        if (typeof json.declared_area_context == 'object')
            space.declared_area_context = json.declared_area_context;

        if (typeof json.slab_offset == 'number')
            space.slab_offset = json.slab_offset;

        if (typeof json.label_position == 'object')
            space.label_position = json.label_position;

        if (json.facings) {
            space.facings = json.facings.map((facing) =>
                facing && facing.ID
                    ? scene.building.facing_types.find(
                        (ft) => ft.ID === facing.ID
                    )
                    : null
            );
            while (space.facings.length < 3) space.facings.push(null);
        } 

        if (json.substrates) {
            space.substrates = json.substrates.map((facing) =>
                facing && facing.ID
                    ? scene.building.get_mmm_compositions().find(
                        (ft) => ft.ID === facing.ID
                    )
                    : null
            );
            while (space.substrates.length < 3) space.substrates.push(null);
        } 

        if (typeof json.heated == 'boolean') space.heated = json.heated;

        if (typeof json.space_label_visibility == 'number')
            space.space_label_visibility = json.space_label_visibility;

        if (json.space_type) {
            space.space_type = scene.building.get_element_type(json.space_type);
        }

        if (typeof (json.space_type_index) == 'number')
            space.space_type_index = json.space_type_index;

        if (json.unique_label)
        {
            space.unique_label = {};
            for (var k in json.unique_label)
                space.unique_label[k] = scene.building.find_unique_space_label(json.unique_label[k]);
        }

        if (json.numerotation_space_segment)
            space.numerotation_space_segment = json.numerotation_space_segment;

        scene.spaces.push(space);
        space.update(true);
        return space;
    }

    /**
     * Unserialize space parents
     * @param {object} json
     * @param {cn_scene} scene
     * @returns
     */
    static unserialize_parent_space(json, scene) {
        if (!json.parent_space) return;
        const parent_space = scene.spaces.find(
            (sp) => sp.ID == json.parent_space
        );
        const space = scene.spaces.find((sp) => sp.ID == json.ID);
        if (space && parent_space) space.parent_space = parent_space;
    }

    get_name_from_space_type() {
        return this.name || (this.space_type ? this.space_type.space_default_name : '');
    }

    //***********************************************************************************
    //**** shape
    //***********************************************************************************
    /**
     *
     * @param {fh_polygon} shape
     * @param {cn_transaction_manager} transaction_manager
     * @returns
     */
    set_inner_polygon(shape, transaction_manager = null) {
        if (cn_compare_polygons(this.inner_polygon, shape)) return;
        if (transaction_manager) transaction_manager.push_item_set(this, "inner_polygon");
        else this["update_date"] = undefined;
        this.inner_polygon = shape;
    }

    /**
     * Returns inner polygon
     * @returns {fh_polygon}
     */
    get_inner_polygon() {
        return this.inner_polygon;
    }

    /**
     * Lazy construction of inner polygon, with piercings
     * @param {cn_storey} storey
     * @returns {fh_polygon}
     */
    get_inner_pierced_polygon(storey) {
        // @ts-ignore
        return this.get_lazy_data(() => {

            const pg = this.get_inner_polygon().clone();
            this._remove_slab_opening(pg, 0, storey);
            return pg;
        }, "inner_polygon_pierced", storey);
    }

    /**
     * Lazy construction of outer polygon
     * @returns {fh_polygon}
     */
    get_outer_polygon() {
        // @ts-ignore
        return this.get_lazy_data(() => {
            const pg = this.get_inner_polygon().clone();
            pg.offset(0.01);

            //*** Loop on contours */
            this.space_segments.forEach(spc => {
                let contour_in = [];
                let contour_out = [];
                const sz = spc.length;
                const contour_pairs = [];
                //*** Loop on space segments of a contour */
                for (var i = 0; i < sz; i++) {
                    //*** expect two segments that match two walls */
                    const sps0 = spc[i];
                    const sps1 = spc[(i + 1) % sz];
                    const w0 = (sps0.wall_side < 2 && sps0.wall && !sps0.wall.wall_type.free) ? sps0.wall : null;
                    const w1 = (sps1.wall_side < 2 && sps1.wall && !sps1.wall.wall_type.free) ? sps1.wall : null;
                    let wp0 = [0, 0];
                    let wp1 = [0, 0];
                    //*** compute geometry for second face of each wall */
                    if (w0)
                        wp0 = cn_add(sps0.points[1], cn_mul(sps0.normal, w0.wall_type.thickness));
                    if (w1)
                        wp1 = cn_add(sps1.points[0], cn_mul(sps1.normal, w1.wall_type.thickness));

                    //*** First case : both walls exist and have a thickness */
                    if (w0 && w1) {
                        const pdot = cn_dot(sps0.direction, sps1.direction);

                        //*** If wall are in the same direction */
                        if (pdot > CN_WALL_COSINE_SHAPE_THRESHOLD) {
                            const nor = cn_normal(cn_add(sps0.direction, sps1.direction));
                            cn_normalize(nor);
                            wp0 = cn_intersect_line(wp0, sps0.direction, sps0.points[1], nor);
                            wp1 = cn_intersect_line(wp1, sps1.direction, sps1.points[0], nor);
                            if (!wp0 || !wp1)
                                console.log("we shouldn't be here!!!");
                        }
                        //*** If walls intersect */
                        else if (pdot >= -CN_WALL_COSINE_SHAPE_THRESHOLD) {
                            wp0 = wp1 = cn_intersect_line(wp0, sps0.direction, wp1, sps1.direction);
                            if (!wp1)
                                console.log("we shouldn't be here!!!");
                        }
                    }

                    //*** End previous contours */
                    if (w0) {
                        contour_in.push(sps0.points[1]);
                        contour_out.push(wp0);
                    }

                    //*** Create new pair of contours */
                    contour_pairs.push([contour_in, contour_out]);

                    //*** start new contour */
                    contour_in = [];
                    contour_out = [];
                    if (w1) {
                        contour_in.push(sps1.points[0]);
                        contour_out.push(wp1);
                    }
                }

                //*** First case : we didn't find any discontinuity. */
                if (contour_pairs.length == 0) {
                    const new_pg = new fh_polygon([0, 0, 0], [0, 0, 1]);
                    new_pg.add_contour(contour_out.map(v => cnx_clone(v)));
                    new_pg.add_contour(contour_in.reverse().map(v => cnx_clone(v)));
                    new_pg.offset(0.01);
                    pg.unites(new_pg);
                }
                //*** There was a discontinuity */
                else {
                    //*** Add end of last contours to start of first one. */
                    if (contour_in.length + contour_out.length) {
                        contour_pairs[0][0] = contour_in.concat(contour_pairs[0][0]);
                        contour_pairs[0][1] = contour_out.concat(contour_pairs[0][1]);
                    }

                    //*** Manage contour pairs */
                    contour_pairs.filter(contour_pair => contour_pair[0].length >= 2 && contour_pair[1].length >= 2).forEach(contour_pair => {
                        contour_out = contour_pair[1];
                        contour_in = contour_pair[0];
                        contour_out.reverse();
                        contour_in = contour_in.concat(contour_out);
                        const new_pg = new fh_polygon([0, 0, 0], [0, 0, 1]);
                        new_pg.add_contour(contour_in.map(v => cnx_clone(v)));
                        new_pg.offset(0.01);
                        pg.unites(new_pg);
                        contour_in = [];
                        contour_out = [];
                    });


                    //*** Loop on wrong space segments of a contour */
                    const range = 1;
                    const sampling = 0.1;
                    const scene = this.scene;

                    //*** Loop on wrong space segments */
                    spc.filter(ss => !ss.wall || ss.wall_side >= 2 || ss.wall.wall_type.free).forEach(ss => {

                        //** We cast ray arouns the middle of the segments. We record every space segment impacted */
                        const space_segments = [];
                        const origin = cn_add(cn_middle(ss.points[0], ss.points[1]), cn_mul(ss.normal, -0.01));
                        for (var axis = 0; axis < 2; axis++) {
                            for (var sense = 0; sense < 2; sense++) {
                                const p = cn_clone(origin);
                                p[axis] = origin[axis];
                                if (sense) p[axis] += range;
                                else p[axis] -= range;
                                for (var x = 0; x < 2 * range; x += sampling) {
                                    p[1 - axis] = origin[1 - axis] - range + x;
                                    const ray_dir = cn_sub(p, origin);
                                    const max_distance = cn_normalize(ray_dir);
                                    const impact = scene.raytrace_spaces(origin, ray_dir, max_distance, this);
                                    if (impact && !space_segments.includes(impact.space_segment))
                                        space_segments.push(impact.space_segment);
                                }
                            }
                        }

                        //*** We build a convex polygon, clipped by all space segments impacted */
                        const clean_pg = new fh_polygon([0, 0, 0], [0, 0, 1]);
                        clean_pg.add_contour([[origin[0] - range, origin[1] - range, 0], [origin[0] + range, origin[1] - range, 0], [origin[0] + range, origin[1] + range, 0], [origin[0] - range, origin[1] + range, 0]]);
                        space_segments.forEach(ssx => {
                            const ss_normal = (cn_dot(ssx.normal, cn_sub(ssx.points[0], origin)) > 0) ? ssx.normal : cn_mul(ssx.normal, -1);
                            const contour = [];
                            let px = cnx_add(origin, cn_mul(ss_normal, cn_dot(ss_normal, cn_sub(ssx.points[0], origin)) + 0.01));
                            px = cnx_sub(px, cn_mul(ssx.direction, 4 * range));
                            contour.push(px);
                            px = cnx_add(px, cn_mul(ssx.direction, 8 * range));
                            contour.push(px);
                            px = cnx_add(px, cn_mul(ss_normal, 8 * range));
                            contour.push(px);
                            px = cnx_sub(px, cn_mul(ssx.direction, 8 * range));
                            contour.push(px);
                            const clipg = new fh_polygon([0, 0, 0], [0, 0, 1]);
                            clipg.add_contour(contour);
                            clean_pg.substracts(clipg);
                        });

                        //*** We unite with the outer polygon */
                        pg.unites(clean_pg);
                    });
                }
            });
            pg.offset(-0.01);
            return pg;
            // @ts-ignore
        }, "outer_polygon", null, this.scene.scene_analyser);
    }

    /**
     * Lazy construction of the outer polygon with piercings.
     * @param {cn_storey} storey
     * @returns {fh_polygon}
     */
    get_outer_pierced_polygon(storey) {
        // @ts-ignore
        return this.get_lazy_data(() => {
            const pg = this.get_outer_polygon().clone();
            this._remove_slab_opening(pg, 0, storey);
            return pg;
        }, "outer_pierced_polygon", storey, this.scene);
    }

    /**
     * Internal : removes slab openings (of the storey and from storey below stairs)
     * @param {fh_polygon} polygon
     * @param {number} z
     * @param {cn_storey} storey;
     */
    _remove_slab_opening(polygon, z, storey = null) {
        if (this.scene && this.scene.slab_openings) {
            this.scene.slab_openings.filter(so => !so.slab_type).forEach(so => {
                var slab_polygon = so.build_3d_polygon(z);
                polygon.substracts(slab_polygon);
            });
        }

        if (!storey) storey = this.scene.storey;
        const storey_below = (storey) ? storey.get_previous_storey() : null;
        if (storey_below) {
            storey_below.scene.stairs.forEach(st => {
                const pg = st.build_3d_slab_opening(z);
                if (pg) polygon.substracts(pg);
            });
            storey_below.scene.spaces.filter(sp => !sp.has_ceiling).forEach(sp => {
                const pg = sp.get_inner_polygon();
                if (pg) polygon.substracts(pg);
            });
        }
    }

    /**
     * Rebuild space lines
     * @param {cn_transaction_manager} transaction_manager
     */
    build_space_segments(transaction_manager) {
        if (transaction_manager && this.space_segments.length)
            transaction_manager.push_item_set(this, "space_segments");
        const old_space_segments = this.space_segments;
        this.space_segments = [];

        //*** initialize facings */
        let different_facings = false;
        if (old_space_segments.length) {
            let common_facing = old_space_segments[0][0].facing;
            if (!old_space_segments.some(slc => slc.some(sl => sl.facing != common_facing))) {
                different_facings = true;
                common_facing = null;
            }
            let common_substrate = old_space_segments[0][0].substrate;
            if (!old_space_segments.some(slc => slc.some(sl => sl.substrate != common_substrate))) {
                different_facings = true;
                common_substrate = null;
            }
        }

        const space = this;

        //*** Space line match between wall and line */
        function make_space_segments(p0, p1) {
            if (cn_manhattan_distance(p0, p1) < 0.001) return [];

            const dummy_space_segment = new cn_space_segment(space);
            dummy_space_segment.points[0] = p0;
            dummy_space_segment.points[1] = p1;
            dummy_space_segment.update();

            const dir = cn_sub(p1, p0);
            const size = cn_normalize(dir);
            let wall = null;

            let x0 = 0;
            let x1 = 0;
            let wall_side = -1;
            for (var nx = 0; nx < 10; nx++) {
                const t = (0.5 + nx) * size / 10;
                const c = cn_add(p0, cn_mul(dir, t));
                wall = space.scene.find_wall(c, 0.01);
                if (!wall) continue;
                const ps = cn_dot(wall.bounds.direction, dir);
                if (Math.abs(ps) > 0.999)
                    wall_side = (ps > 0) ? 0 : 1;
                else
                    wall_side = (cn_dot(dir, wall.bounds.normal) > 0) ? 3 : 2;
                const segment = wall.get_side_segment(wall_side);
                x0 = cn_dot(dir, cn_sub(segment[0], p0));
                x1 = cn_dot(dir, cn_sub(segment[1], p0));
                if (x0 > size - 0.001 || x1 < 0.001) {
                    wall = null;
                    continue;
                }
                if (x0 < 0) x0 = 0;
                if (x1 > size) x1 = size;
                break;
            }

            if (!wall)
                return [dummy_space_segment];

            let space_segments = [];
            const pp0 = cn_add(p0, cn_mul(dir, x0));
            const pp1 = cn_add(p0, cn_mul(dir, x1));
            if (x0 > 0.001) space_segments = space_segments.concat(make_space_segments(p0, pp0));
            if (x1 - x0 > 0.001) {
                const space_segment = new cn_space_segment(space);
                space_segment.points[0] = pp0;
                space_segment.points[1] = pp1;
                space_segment.wall = wall;
                space_segment.wall_side = wall_side;
                space_segment.update();
                space_segment.update_wall_positions();
                wall.space_segments.push(space_segment);
                space_segments.push(space_segment);
            }
            if (x1 < size - 0.001) space_segments = space_segments.concat(make_space_segments(pp1, p1));
            return space_segments;
        }

        /** Build space lines */
        let offset = 0;
        for (var nct = 0; nct < this.inner_polygon.contour_sizes.length; nct++) {
            const sz = this.inner_polygon.contour_sizes[nct];
            let dnv = (this.outside) ? 1 : sz - 1;
            let space_segment_contour = [];
            for (var nv = 0; nv < sz; nv++) {
                const p0 = this.inner_polygon.contour_vertices[offset + nv];
                const p1 = this.inner_polygon.contour_vertices[offset + ((nv + dnv) % sz)];
                const new_space_segments = make_space_segments(p0, p1);
                if (!this.outside) new_space_segments.reverse();
                space_segment_contour = space_segment_contour.concat(new_space_segments);
            }
            if (!this.outside) space_segment_contour.reverse();

            this.space_segments.push(space_segment_contour)
            offset += sz;
        }

        //*** Build up IDs */
        const done_ids = [];
        this.space_segments.forEach(sslist => sslist.forEach(ss => {
            let base_id = ss.space.ID;
            if (ss.wall) base_id += ss.wall.ID + ss.wall_side;
            base_id = 'cn' + cn_md5(base_id);
            while (done_ids.includes(base_id))
                base_id = 'cn' + cn_md5(base_id + "gg");
            done_ids.push(base_id);
            ss.ID = base_id;
        }));

        //*** initialize facings */
        if (different_facings) this.collect_facing(old_space_segments);
    }

    /**
     * Try to match facings from a list of old facings (from a space)
     * @param {cn_space_segment[][]} space_segments
     */
    collect_facing(space_segments) {
        if (!space_segments || space_segments.length == 0) return;
    }

    /**
     * Gets space usage
     * @returns {string}
     */
    get_space_usage() {
        if (this.space_type) return this.space_type.space_usage;
        if (this.space_usage) return this.space_usage;
        return null;
    }

    //***********************************************************************************
    //**** Build name
    //***********************************************************************************
    /**
     * Returns the base name of the space (before any numerotation)
     * @returns {string}
     */
    get_base_name() {
        if (this.outside) return SPACE_EXTERIOR_LABEL;
        if (this.name != "") return this.name;
        return cn_plugin_option.default_space_label;
    }

    get_storey_identifyer(storey) {
        let name = this.get_storey_name(storey);

        name += ".";
        if (this.outside)
            name += 'X';
        else
            name += this.index;

        return name;
    }

    /**
     * Returns the storey numerotation of the space
     * @returns {string}
     */
    get_storey_name(storey) {
        if (storey.exterior === true)
            return SPACE_EXTERIOR_LABEL.toLocaleLowerCase();
        return "" + storey.storey_index;
    }

    get_name_in_zone(storey) {
        if (!this.space_type || this.space_type.space_default_name == "") return "";
        let name = this.space_type.space_default_name;
        let start_numerotation = (this.space_type.numerotation >= 0) ? this.space_type.numerotation : 1;
        if (this.custom_index >= 0) return `${name} ${this.space_type.prefix}${this.custom_index + start_numerotation}`;

        let default_zoning_type = extension_instance.zone.get_default_zone_tool().property;
        let this_zone = this.get_zone(default_zoning_type, storey);
        let spaces_in_zone = storey.scene.spaces.filter(sp => sp.space_type == this.space_type && sp.get_zone(default_zoning_type, storey) == this_zone);
        if (spaces_in_zone.length == 1) return name;
        let custom_indices = spaces_in_zone.filter(s => s.custom_index >= 0).map(s => s.custom_index).sort();
        spaces_in_zone = spaces_in_zone.filter(s => s.custom_index < 0);
        spaces_in_zone.sort((a, b) => a.space_type_index - b.space_type_index);
        let index = spaces_in_zone.indexOf(this);
        custom_indices.forEach(c => {
            if (c <= index) index++;
        });
        return `${name} ${this.space_type.prefix}${start_numerotation + index}`;
    }

    /**
     * Returns generic name
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {string}
     */
    get_generic_name(storey) {
        return this.get_base_name() + " " + this.get_storey_identifyer(storey);
    }

    get_name(storey) {
        let name = this.get_generic_name(storey);

        let zone_name = this.get_name_in_zone(storey);
        if (zone_name != "") name += ` (${zone_name})`;

        return name;
    }

    /**
     * Returns code name
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {string}
     */
    build_readable_id(storey) {
        let name = storey.build_readable_id(storey) + "E";
        if (this.outside)
            name += "X";
        else
            name += this.index;
        return name;
    }

    /**
     * Returns available ceiling height (HSP in french), i.e. ceiling height (or storey height if absent) minus slab_offset
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {number}
     */
    get_real_ceiling_height(storey) {
        const hsp = this.build_space_volume(storey).get_bounding_box().size[2];
        if (this.ceiling_type && this.ceiling_height < hsp)
            return this.ceiling_height;
        return hsp;
    }

    //***********************************************************************************
    /**
     * Returns declared area (if exists), or actual area, collecting children spaces
     * @param {boolean} including_children
     * @returns {number}
     */
    get_area(including_children = true) {
        if (this.declared_area > 0) return this.declared_area;
        var area = this.area;
        if (including_children)
            this.get_children_spaces().forEach((s) => (area += s.area));
        return area;
    }

    //***********************************************************************************
    /**
     * Returns plain area, collecting children spaces
     * @param {boolean} including_children
     * @returns {number}
     */
    get_plain_area(including_children = true) {
        var area = this.plain_area;
        if (including_children)
            this.get_children_spaces().forEach((s) => (area += s.plain_area));
        return area;
    }

    /**
     *Returns area from a given area context
     * @param {cn_area_context} area_context
     * @param {number} sub_context
     * @param {cn_storey} storey
     * @param {boolean} including_children
     * @returns {number}
     */
    get_area_from_context(
        area_context,
        sub_context,
        storey,
        including_children = true
    ) {
        var area = 0;
        if (this.is_declared_area_from_context(area_context, sub_context))
            area = this.declared_area_context[area_context.code][sub_context];
        else {
            const space_area = area_context.get_space_area(storey, this);
            if (space_area && sub_context < space_area.sub_areas.length)
                area += space_area.sub_areas[sub_context];
        }

        if (including_children)
            this.get_children_spaces().forEach(
                (s) =>
                (area += s.get_area_from_context(
                    area_context,
                    sub_context,
                    storey,
                    false
                ))
            );

        return area;
    }

    /**
     *Returns true if this area has been declared
     * @param {cn_area_context} area_context
     * @param {number} sub_context
     * @returns {boolean}
     */
    is_declared_area_from_context(area_context, sub_context) {

        if (typeof (this.declared_area_context[area_context.code]) != "undefined" &&
            typeof (this.declared_area_context[area_context.code][sub_context]) == "number") {
            let dec = this.declared_area_context[area_context.code][sub_context];
            if (dec >= 0)
                return true;
        }

        return false;
    }

    /**
     * Sets declared area from a given area context
     * If value is negative, the used area will be the one computed.
     * @param {number} value
     * @param {cn_area_context} area_context
     * @param {number} sub_context
     * @returns
     */
    set_declared_area_from_context(value, area_context, sub_context) {
        if (typeof (this.declared_area_context[area_context.code]) == "undefined")
            this.declared_area_context[area_context.code] = [];
        while (sub_context >= this.declared_area_context[area_context.code].length)
            this.declared_area_context[area_context.code].push(-1);
        this.declared_area_context[area_context.code][sub_context] = value;
    }

    /**
     * Returns regulation areas
     * @param {cn_storey} storey
     * @returns {{label: string, value: number}[][]}
     */
    get_areas(storey) {
        return storey.building.area_contexts.map(ac => {
            if (ac.sub_labels.length) {
                return ac.sub_labels.map((lbl, i) => { return { label: lbl, value: this.get_area_from_context(ac, i, storey, false) }; });
            }
            else
                return [{ label: ac.label, value: this.get_area_from_context(ac, 0, storey, false) }];
        });
    }

    //***********************************************************************************
    /**
     * Returns declared perimeter (if exists), or actual perimeter
     */
    get_perimeter() {
        if (this.declared_perimeter > 0) return this.declared_perimeter;
        return this.perimeter;
    }

    /**
     * Returns true if heated is relevant (i.e. space is heatable)
     * @param {cn_storey} storey
     * @returns {boolean}
     */
    is_heated_relevant(storey = null) {
        if (this.parent_space)
            return this.parent_space.is_heated_relevant(storey);
        if (this.outside) return false;
        if (!this.has_roof) return false;
        if (!this.is_indoor(storey)) return false;
        return true;
    }

    /**
     * Returns true if heated
     * @param {cn_storey} storey
     * @returns {boolean}
     */
    is_heated(storey = null) {
        if (this.parent_space) return this.parent_space.is_heated(storey);
        if (this.outside) return false;
        if (!this.has_roof) return false;
        if (!this.is_indoor(storey)) return false;
        return this.space_type.heated;
    }

    /**
     * Returns true if space is indoor
     * @param {cn_storey} storey
     * @returns
     */
    is_indoor(storey = null) {
        if (!this.indoor) return false;
        if (storey) return this._indoor[storey.ID];
        if (this.parent.storey) return this._indoor[this.parent.storey.ID];
        return true;
    }

    /**
     * Returns true if space is indoor
     * @param {cn_storey} storey
     * @returns
     */
    is_roof(storey = null) {
        if (!this.has_roof) return false;
        if (storey) return this._has_roof[storey.ID];
        if (this.parent.storey) return this._has_roof[this.parent.storey.ID];
        return true;
    }

    /**
     * Returns true if this space has a subface.
     * @param {cn_storey} storey
     */
    is_sub_face(storey) {
        let storey_below = storey.get_relative_storey(-1);
        if (!storey_below) return false;
        return storey.slabs.some(slab => slab.spaces[1] == this && (slab.spaces[0] == null || !slab.spaces[0].is_roof(storey_below)));
    }

    /**
     * returns true if ceiling is in a non heated area
     * @returns {boolean}
     */
    is_unheated_ceiling() {
        return (this.ceiling_type) ? !this.regulated_ceiling : false;
    }

    /**
     * Sets heated, with all consequences
     * @param {boolean} value
     * @param {cn_transaction_manager} transaction_manager
     */
    set_heated(value, transaction_manager) {
        if (this.parent_space) return;

        transaction_manager.push_item_set(this, ['heated']);
        this.heated = value;

        this.get_children_spaces().forEach((sp) => {
            transaction_manager.push_item_set(sp, ['heated']);
            sp.heated = value;
        });
    }

    /**
     * Copy characteristics (non geometrical) of another space
     * @param {cn_space} other_space
     */
    copy_parameters(other_space) {
        this.facings = other_space.facings.concat([]);
        this.substrates = other_space.substrates.concat([]);
        this.slab_offset = other_space.slab_offset;
        this.ceiling_height = other_space.ceiling_height;
        this.ceiling_type = other_space.ceiling_type;
        this.space_type = other_space.space_type;
        this.heated = other_space.heated;
        this.space_usage = other_space.space_usage;
        this.has_ceiling = other_space.has_ceiling;
        this.space_label_visibility = other_space.space_label_visibility;
        Object.entries(this.scene.building.zones).forEach(([_, zones]) => {
            if (this.scene.storey) {
                zones.forEach((zone) => {
                    if (
                        zone.rooms.some(
                            (room) =>
                                room.storey === this.scene.storey.ID &&
                                room.space == other_space.ID
                        )
                    ) {
                        zone.add_room(this.ID, this.scene.storey.ID);
                    }
                });
            }
        });
    }

    /**
     * Collect all data from another space that will soon disapear
     * @param {cn_space} other_space
     */
    collect_space_data(other_space) {
        this.scene.object_instances.forEach((oi) => {
            if (oi.virtual && oi.space == other_space) oi.space = this;
        });
    }

    /**
     * returns the zone of that space in given storey.
     * @param {string} zoning_type
     * @param {cn_storey} storey
     * @returns {cn_zone}
     */
    get_zone(zoning_type, storey) {
        const zones = storey.building.zones[zoning_type];
        if (zones) {
            const zone = zones.find((z) =>
                z.rooms.some((r) => r.storey == storey.ID && r.space == this.ID)
            );
            if (zone) return zone;
        }
        return null;
    }

    /**
     * Sets zone for a given space. The new zone can be null (just remove that space from current zone)
     * Will also set the zone fir children spaces.
     * @param {string} zoning_type
     * @param {cn_zone} zone
     * @param {cn_storey} storey
     * @param {cn_transaction_manager} transaction_manager
     * @returns
     */
    set_zone(zoning_type, zone, storey, transaction_manager = null) {
        //*** check previous zone */
        const previous_zone = this.get_zone(zoning_type, storey);
        if (previous_zone == zone) return;

        //*** Send event to warn zone change */
        if (!this.parent_space && zoning_type === extension_instance.zone.get_default_zone_tool().property) {
            extension_instance.space.on_change_zone(this, previous_zone, zone);
        }

        //*** Remove from previous zone */
        if (previous_zone) {
            const index = previous_zone.rooms.findIndex(
                (r) => r.storey == storey.ID && r.space == this.ID
            );
            if (index >= 0) {
                if (transaction_manager)
                    transaction_manager.push_item_set(previous_zone, 'rooms');
                previous_zone.rooms.splice(index, 1);
            }
        }

        //*** insert into new zone */
        if (zone) {
            if (transaction_manager)
                transaction_manager.push_item_set(zone, 'rooms');
            zone.add_room(this.ID, storey.ID);
        }

        //*** recursive call */
        this.get_children_spaces().forEach((sp) =>
            sp.set_zone(zoning_type, zone, storey, transaction_manager)
        );
    }

    //***********************************************************************************
    //**** update geometry
    //***********************************************************************************
    update(force = false) {
        this.update_numerotation();

        if (!force) {
            if (typeof (this["update_date"]) != undefined && this.up_to_date(this["update_date"], "inner_polygon")) return;
            this["update_date"] = CN_CURRENT_DATE;
        }

        //*** Update bounding box */
        this._bounding_box = new cn_box();
        const bb = this.inner_polygon.get_bounding_box();
        this._bounding_box.posmin = cn_clone(bb.position);
        this._bounding_box.size = cn_clone(bb.size);

        //*** Update area and perimeter */
        this.area = this.inner_polygon.get_area();
        this.perimeter = this.inner_polygon.get_perimeter();

        if (Math.abs(this.area - this._area_for_label_position) > 0.1) {
            if (this._area_for_label_position >= 0)
                this.label_position = [0, 0];

            this._area_for_label_position = this.area;
        }
        this.measure.update();
    }

    update_deep() {
        this.update();
        if (!this.outside) {
            this.center = this.find_best_center();
        }

        //*** build this  polygon
        var polygon = this.inner_polygon.clone();

        //*** build slab opening polygon polygon
        this._remove_slab_opening(polygon, 0);
        this.scene.columns.forEach(col => polygon.substracts(col.get_footprint()));

        this._full_facing_floor_polygon = polygon.clone();

        this._facing_floor_polygons = [];

        //*** remove storey's ignored area */
        if (
            this.scene &&
            this.scene.storey &&
            this.scene.storey.ignored_polygon
        )
            polygon.substracts(this.scene.storey.ignored_polygon);

        this.plain_area = polygon.get_area();

        var pgs = polygon.split();

        this.polygons = [];
        for (var i = 0; i < pgs.length; i++) {
            var ctr = cn_contour.build_from_polygon(pgs[i]);
            this.polygons.push(ctr);
        }
        if (!this.outside) this._update_facing_trimmings();

        //*** parenthood */
        while (this.parent_space && this.parent_space.parent_space)
            this.parent_space = this.parent_space.parent_space;
        if (
            this.parent_space &&
            !this.get_parent_candidates().includes(this.parent_space)
        )
            this.parent_space = null;
    }

    /**
     * Returns all doors that opens to this space.
     * @returns {cn_opening[]}
     */
    get_doors() {
        return this.scene.walls.flatMap(wall => wall.openings.filter(o => o.valid && o.opening_type.category === 'door' &&
            o.wall_interface && o.wall_interface.spaces.includes(this)
        ));
    }

    _update_facing_trimmings() {
        this.scene.facing_trimmings
            .filter((ft) => ft.placement == CN_FACING_TRIMMING_PLACEMENT_FLOOR)
            .forEach((ft) => {
                if (this._bounding_box.intersects(ft.get_bounding_box())) {
                    const pg = ft._polygon.clone();
                    pg.intersects(this._full_facing_floor_polygon);
                    if (pg.get_area() > 0.01) {
                        if (this._facing_floor_polygons.length == 0) {
                            this._facing_floor_polygons = [
                                this._full_facing_floor_polygon.clone(),
                            ];
                        }
                        this._facing_floor_polygons.forEach((fpg) =>
                            fpg.substracts(ft._polygon)
                        );
                        this._facing_floor_polygons.push(pg);
                        pg['facing_trimming'] = ft;
                    }
                }
            });
    }

    /**
     * Returns the floor polygons of the space.
     * If a polygon comes from a facing trimming, it's facing trimming isin the 'facing_trimming' field of the polygon.
     * @returns {Array<fh_polygon>}
     */
    get_floor_facing_polygons() {
        return this._facing_floor_polygons.length > 0
            ? this._facing_floor_polygons
            : [this._full_facing_floor_polygon];
    }

    /**
     * Returns the list of potential parent spaces.
     * @returns {Array<cn_space>}
     */
    get_parent_candidates() {
        const h = this.is_heated_relevant();
        const parent_candidates = [];
        this.scene.walls.forEach(wall => wall.wall_interfaces.forEach(wi => {
            if (wi.spaces[0] == this || wi.spaces[1] == this) {
                wi.spaces.forEach(sp => {
                    while (sp.parent_space) sp = sp.parent_space;
                    if (sp != this && h == sp.is_heated_relevant()) {
                        if (!parent_candidates.includes(sp))
                            parent_candidates.push(sp);
                    }
                });
            }
        }));
        return parent_candidates;
    }

    get_children_spaces() {
        return this.scene.spaces.filter((sp) => sp.parent_space == this);
    }

    /**
     * Sets the space as parent, with all given consequences.
     * @param {cn_space} parent
     * @param {cn_transaction_manager} transaction_manager
     */
    set_parent_space(parent, transaction_manager) {
        while (parent && parent.parent_space) parent = parent.parent_space;
        transaction_manager.push_item_set(this, ['parent_space']);
        this.parent_space = parent;

        if (parent) {
            //*** applies the zoning of the parent */
            const clone_storeys = this.scene.storey.building.storeys.filter(
                (s) => s.scene == this.scene
            );
            if (!clone_storeys.includes(this.scene.storey))
                clone_storeys.push(this.scene.storey);
            cn_zone.get_zoning_types().forEach((zt) => {
                clone_storeys.forEach((st) => {
                    this.set_zone(
                        zt,
                        parent.get_zone(zt, st),
                        st,
                        transaction_manager
                    );
                });
            });
        }
    }

    //***********************************************************************************
    //**** returns the clockwise contour
    //***********************************************************************************
    get_clockwise_contour() {
        return null;
    }

    //***********************************************************************************
    //**** Draw the space in svg
    //***********************************************************************************
    draw(
        camera,
        add_classes = [],
        fill_color = '',
        allow_numerotation = true,
        allow_outside = false
    ) {
        var html = '';
        if (this.outside && !allow_outside) {
            html += this.draw_numerotation(camera, allow_numerotation);
            return html;
        }

        if (!add_classes.includes('draw_standalone') && this.parent_space) {
            html += this._draw_highlight(camera, add_classes);
            return html;
        }

        html += this._draw_forced(
            camera,
            add_classes,
            fill_color,
            allow_numerotation,
            allow_outside
        );

        if (!add_classes.includes('no_children'))
            this.get_children_spaces().forEach(
                (sp) =>
                (html += sp._draw_forced(
                    camera,
                    add_classes,
                    fill_color,
                    allow_numerotation,
                    allow_outside
                ))
            );

        //*** LET THIS FOR TEST */
        //this.space_segments.forEach(slc => slc.forEach(sl => html += sl.draw(camera)));

        return html;
    }

    _draw_forced(
        camera,
        add_classes = [],
        fill_color = '',
        allow_numerotation = true,
        allow_outside = false
    ) {
        var html = '';
        var draw_class = 'space';
        const is_export = add_classes.includes('exp');
        if (!this.has_roof && !this.facings[0]) draw_class += ' no_roof';
        if (!this.indoor && !is_export) draw_class += ' outdoor';

        if (add_classes)
            draw_class +=
                ' ' +
                add_classes
                    .filter((c) => c != 'selected' && c != 'mouseover')
                    .join(' ');

        html += this._draw_path(camera, draw_class, fill_color, is_export);

        html += this._draw_highlight(camera, add_classes);

        html += this.draw_numerotation(camera, allow_numerotation);
        return html;
    }

    _draw_highlight(camera, add_classes) {
        var html = '';
        if (
            add_classes.includes('selected') ||
            add_classes.includes('mouseover')
        ) {
            var draw_class = 'space';
            if (add_classes.includes('selected')) draw_class += ' selected';
            if (add_classes.includes('mouseover')) draw_class += ' mouseover';
            html += this._draw_path(camera, draw_class, '', false, true);
        }
        return html;
    }

    _draw_path(
        camera,
        draw_class,
        fill_color = '',
        is_export = false,
        only_contour = false
    ) {
        var html = '';
        const polygons =
            this._facing_floor_polygons.length > 0 && !only_contour
                ? this._facing_floor_polygons
                : [this._full_facing_floor_polygon];
        polygons.forEach((polygon) => {
            polygon.compute_contours();
            const facing = polygon['facing_trimming']
                ? polygon['facing_trimming'].facing
                : this.facings[0];
            var fc = `fill="none"`;

            if (!only_contour) {
                fc = fill_color;
                if (
                    camera.show_facings &&
                    !is_export &&
                    fill_color == '' &&
                    facing &&
                    draw_class.indexOf('mouseover') < 0 &&
                    draw_class.indexOf('selected') < 0
                ) {
                    var sz = camera.world_to_screen_scale.toFixed(0);
                    sz = camera.world_to_screen_scale.toFixed(0);
                    var pt = camera.world_to_screen([0, 0]);
                    var ptx = pt[0].toFixed(0);
                    var pty = pt[1].toFixed(0);
                    const facing_pattern_and_style =
                        generate_pattern_for_facing(
                            facing.texture,
                            facing.color,
                            sz,
                            sz,
                            ptx,
                            pty,
                            facing.ID
                        );
                    html += facing_pattern_and_style.pattern;
                    fc = facing_pattern_and_style.style;
                }
            }
            html += `<path class="${draw_class}" ${fc} d="`;
            var offset = 0;
            for (var nctr = 0; nctr < polygon.contour_sizes.length; nctr++) {
                const sz = polygon.contour_sizes[nctr];
                for (var j = 0; j < sz; j++) {
                    if (j == 0) html += 'M ';
                    else if (j == 1) html += 'L ';
                    var p = camera.world_to_screen(
                        polygon.contour_vertices[offset + j]
                    );
                    html += '' + p[0] + ' ' + p[1] + ' ';
                }
                html += 'Z ';
                offset += sz;
            }
            html += `" fill-rule="evenodd" />`;
        });
        return html;
    }

    get_numerotation_space_segment() {
        let num_ss = null; 
        this.space_segments.some(sslist => {
            num_ss = sslist.find(ss => ss.ID == this.numerotation_space_segment);
            return num_ss != null;
        });
        if (num_ss) return num_ss;

        let best_criterion = 100;
        let previous_wall_free = false;
        this.space_segments.forEach(sslist => {
            sslist.forEach(ss => {
                if (ss.wall_side>=2) return;
                if (!ss.wall) return;
                let wis = ss.wall.wall_interfaces.filter(wi => wi.space_segments.includes(ss));
                if (!wis.length) return;
                let criterion = 100;
                let openings = ss.wall.openings.filter(op => wis.includes(op.wall_interface));
                openings.forEach(op => {
                    if (op.get_z() == 0)
                    {
                        //*** Criterion 0 for outside door */
                        if (best_criterion > 0 && (!op.wall_interface.spaces[0].has_ceiling || !op.wall_interface.spaces[1].has_ceiling))
                            criterion = 0;
                        //*** Criterion 1 for door */
                        else criterion = 1;
                    }
                    //*** Criterion 2 for free opening */
                    else if (op.opening_type.free && best_criterion > 2)
                        criterion = 2;
                    //*** Criterion 4 for opening */
                    else if (best_criterion > 4)
                        criterion = 4;
                });
                //*** Criterion 3 for first wall after free wall */
                if (best_criterion > 3 && !ss.wall.wall_type.free && previous_wall_free) 
                    criterion = 3;
                if (ss.wall.wall_type.free) 
                    previous_wall_free = true;
                //*** Criterion 5 for any wall */
                else if (best_criterion > 5)
                    criterion = 5;
                if (criterion < best_criterion) {
                    best_criterion = criterion;
                    num_ss = ss;
                }
            });
        });

        return num_ss;
    }

    update_numerotation() {
        //if (!this.outside) {
        this.space_segments.forEach(sslist => {
            sslist.forEach(ss => ss.numerotation = "");
        });

        let main_door_space_segment = this.get_numerotation_space_segment();

        this._numerotation = [];
        if (cn_plugin_option.numerotation && main_door_space_segment) {
            let num = { prefix: '', index: '' };
            const contourWithMainDoor = this.space_segments.find(ss_list => ss_list.includes(main_door_space_segment));
            [...this.space_segments]
                .sort((a, b) => {
                    if (a === contourWithMainDoor) {
                        return -1;
                    } else if (b === contourWithMainDoor) {
                        return 1;
                    } else {
                        return b.reduce((acc, ss) => acc + ss.size, 0) - a.reduce((acc, ss) => acc + ss.size, 0);
                    }
                })
                .forEach((ss_list, ic) => {
                    num.index = '';
                    if (ss_list.length) {
                        let space_segments = ss_list.filter(ss => ss.wall);

                        //*** the list of space segments starts at the door segment */
                        let first_space_segment = main_door_space_segment;
                        if (ic !== 0) {
                            first_space_segment = null;
                            var min_position = 0;
                            space_segments.forEach(ss => {
                                const x = ss.points[0][0] - ss.points[0][1];
                                if (!first_space_segment || x < min_position) {
                                    first_space_segment = ss;
                                    min_position = x;
                                }
                            });
                        }
                        let first_index = space_segments.indexOf(first_space_segment);

                        //*** check parallel space segments */
                        while (space_segments[first_index].continues(space_segments[(first_index + space_segments.length - 1) % space_segments.length])) {
                            first_index = (first_index + space_segments.length - 1) % space_segments.length;
                        }

                        //*** Change the start of space segments */
                        if (first_index >= 0) {
                            space_segments = [
                                ...space_segments.slice(first_index),
                                ...space_segments.slice(0, first_index),
                            ];
                        }

                        //*** Apply numerotation */
                        let last_num = null;
                        for (var i = 0; i < space_segments.length; i++) {
                            if (!space_segments[i].can_have_numerotation()) continue;
                            if (i == 0 || !space_segments[i].continues(space_segments[i - 1])) {
                                num = this._getNextIndex(num.prefix, num.index);
                                last_num = new cn_space_numerotation(space_segments[i], num.prefix + num.index);
                                this._numerotation.push(last_num);
                            }
                            else if (last_num) {
                                last_num.p1 = space_segments[i].points[1];
                            }
                            space_segments[i].numerotation = num.prefix + num.index
                        }

                        //*** next contour : add one letter */
                        num.prefix = String.fromCharCode(
                            (num.prefix.charCodeAt(0) || 64) + 1
                        );
                    }
                });
        }
    }

    /**
     * Draws numerotation
     * @param {cn_camera} camera
     * @param {boolean} allow_numerotation
     */
    draw_numerotation(camera = null, allow_numerotation = true) {
        let html = '';
        if (cn_plugin_option.numerotation && allow_numerotation) {
            this._numerotation.forEach(num => html += num.draw(camera));
        }
        return html;
    }

    _getNextIndex(prefix, index) {
        let asciiCodeIndex = (index.charCodeAt(0) || 64) + 1;
        let px = prefix;
        if (asciiCodeIndex > 90) {
            asciiCodeIndex = 65;
            px = String.fromCharCode((px.charCodeAt(0) || 64) + 1);
        }
        const idx = String.fromCharCode(asciiCodeIndex);
        return { prefix: px, index: idx };
    }

    draw_main_door_selected_space(camera) {
        let result = '';
        return result;
    }

    draw_slab_opening(camera) {
        if (this.has_ceiling) return '';
        return this._draw_path(camera, 'slab_opening');
    }

    /**
     * Draws the measures of the space
     * @param {cn_camera} camera
     * @returns {string}
     */
    draw_measures(camera, extra_classes) {
        let html = "";
        this.space_segments.flat().filter(ss => ss.size > camera.measure_limit).forEach(ss => {
            html += camera.draw_measure(ss.points[0], ss.points[1], null, false, true, extra_classes);
        });
        return html;
    }

    //***********************************************************************************
    //**** Contains one point
    //***********************************************************************************
    contains(p, inner = false) {
        if (!this._bounding_box.contains_point(p)) return this.outside;
        return this.inner_polygon.contains(cnx_clone(p)) != this.outside;
    }

    //***********************************************************************************
    //**** Contains one point
    //***********************************************************************************
    contains_segment(p0, p1, inner = false) {
        if (!this.contains(p0, inner)) return false;
        if (!this.contains(p1, inner)) return false;
        var dir = cn_sub(p1, p0);
        var max_distance = cn_normalize(dir);
        if (max_distance < 0.001) return true;

        if (
            this.raytrace(
                cn_add(p0, cn_mul(dir, 0.001)),
                dir,
                max_distance - 0.002,
                inner
            )
        )
            return false;
        return true;
    }

    //***********************************************************************************
    //**** Contains
    //***********************************************************************************
    contained_by_box(box) {
        if (this.outside) return false;
        for (var k = 0; k < 2; k++) {
            if (this._bounding_box.posmin[k] < box.posmin[k]) return false;
            if (this._bounding_box.posmin[k] + this._bounding_box.size[k] > box.posmin[k] + box.size[k]) return false;
        }
        return true;
    }

    //***********************************************************************************
    //**** get box
    //***********************************************************************************
    get_bounding_box() {
        return this._bounding_box;
    }

    /**
     * Returns a solid that matches the inner volume of the space.
     * Lazy calculation
     * @param {cn_storey} storey
     * @param {boolean} thermal_volume
     * @returns {fh_solid}
     */
    build_solid(storey, thermal_volume = false) {
        if (thermal_volume) return this.build_thermal_volume(storey);
        return this.build_space_volume(storey);
    }

    /**
     * Lazyly computes thermal volume.
     * @param {cn_storey} storey
     * @returns {fh_solid}
     */
    build_thermal_volume(storey) {
        // @ts-ignore
        return this.get_lazy_data(
            () => {
                return this._build_thermal_volume(storey);
            },
            'thermal_volume',
            storey
        );
    }

    /**
     * Lazyly computes space volume.
     * @param {cn_storey} storey
     * @returns {fh_solid}
     */
    build_space_volume(storey) {
        // @ts-ignore
        return this.get_lazy_data(
            () => {
                return this._build_space_volume(storey);
            },
            'space_volume',
            storey
        );
    }

    /**
     * Lazyly computes ceiling space volume.
     * @param {cn_storey} storey
     * @returns {fh_solid}
     */
    build_ceiling_solid(storey) {
        // @ts-ignore
        return this.get_lazy_data(
            () => {
                return this._build_ceiling_volume(storey);
            },
            'ceiling_volume',
            storey
        );
    }

    /**
     *
     * @param {cn_storey} storey
     */
    _build_thermal_volume(storey) {
        var footprint = this.get_inner_polygon();
        if (this.slab_offset != 0) {
            footprint = footprint.clone();
            const matrix = new fh_matrix();
            matrix.load_translation([0, 0, this.slab_offset]);
            footprint.apply_matrix(matrix);
        }

        var roof_volume = null;
        var h = 0;
        if (!this.is_roof(storey)) h = storey.height - this.slab_offset;
        else {
            roof_volume = storey.build_thermal_volume();
            const bb = roof_volume.get_bounding_box();
            h = bb.position[2] + bb.size[2] - this.slab_offset;
        }
        if (h < 0.01) h = 0.01;
        var solid = new fh_solid();
        solid.extrusion(footprint, [0, 0, h]);
        if (roof_volume) solid.intersects(roof_volume);
        return solid;
    }

    /**
     *
     * @param {cn_storey} storey
     */
    _build_space_volume(storey) {
        const vol = this.build_thermal_volume(storey);
        const bb = vol.get_bounding_box();
        if (!this.ceiling_type || bb.size[2] < this.ceiling_height)
            return vol;
        const solid = new fh_solid();
        vol.get_faces().forEach((f) => solid.add_face(f.clone()));
        const bounding_vol = new fh_solid();
        bounding_vol.brick(
            fh_sub(bb.position, [0.1, 0.1, 0.1]),
            [bb.size[0] + 0.2, 0, 0],
            [0, bb.size[1] + 0.2, 0],
            [0, 0, this.ceiling_height + 0.1]
        );
        solid.intersects(bounding_vol);

        /** remove volumes of upper openings */
        var openings = this._collect_openings(storey);
        if (openings.length) {
            var footprint = this.get_inner_polygon();
            let roof_volume = storey.build_thermal_volume();
            let roof_bb = roof_volume.get_bounding_box();
            openings.forEach(opening => {
                const op = opening.clone();
                op.project(
                    [0, 0, roof_bb.position[2] - 0.1],
                    [0, 0, 1],
                    [0, 0, 1]
                );
                op.intersects(footprint);
                if (op.get_area() > 0.01) {
                    let opening_solid = new fh_solid();
                    opening_solid.extrusion(op, [0, 0, roof_bb.size[2] + 0.2]);
                    opening_solid.intersects(roof_volume);
                    solid.unites(opening_solid);
                }
            });
        }

        return solid;
    }

    /**
     *
     * @param {cn_storey} storey
     */
    _build_ceiling_volume(storey) {
        const vol = this.build_thermal_volume(storey);
        const bb = vol.get_bounding_box();
        if (!this.ceiling_type || bb.size[2] < this.ceiling_height)
            return vol;
        const solid = new fh_solid();
        vol.get_faces().forEach((f) => solid.add_face(f.clone()));
        const bounding_vol = new fh_solid();
        bounding_vol.brick(
            fh_sub(bb.position, [0.1, 0.1, 0.1]),
            [bb.size[0] + 0.2, 0, 0],
            [0, bb.size[1] + 0.2, 0],
            [0, 0, this.ceiling_height + 0.1]
        );
        solid.substracts(bounding_vol);

        /** remove volumes of upper openings */
        var openings = this._collect_openings(storey);
        if (openings.length) {
            var footprint = this.get_inner_polygon();
            let roof_volume = storey.build_thermal_volume();
            let roof_bb = roof_volume.get_bounding_box();
            openings.forEach(opening => {
                const op = opening.clone();
                op.project(
                    [0, 0, roof_bb.position[2] - 0.1],
                    [0, 0, 1],
                    [0, 0, 1]
                );
                op.intersects(footprint);
                if (op.get_area() > 0.01) {
                    let opening_solid = new fh_solid();
                    opening_solid.extrusion(op, [0, 0, roof_bb.size[2] + 0.2]);
                    solid.substracts(opening_solid);
                }
            });
        }
        return solid;
    }

    /**
     * Builds polygons between heated volume and non heated ceiling.
     * @param {cn_storey} storey
     * @returns {fh_polygon[]}
     */
    build_ceiling_interfaces(storey) {
        // @ts-ignore
        return this.get_lazy_data(
            () => {
                return this._build_ceiling_interfaces(storey);
            },
            'ceiling_interfaces',
            storey
        );
    }

    _build_ceiling_interfaces(storey) {
        let interfaces = [];

        if (!this.ceiling_type) return interfaces;

        const thermal_volume = this.build_thermal_volume(storey);
        let roof_bb = thermal_volume.get_bounding_box();

        var ceiling = thermal_volume.plane_intersection([0, 0, this.slab_offset + this.ceiling_height], [0, 0, 1]);
        if (!ceiling || ceiling.get_area() < 0.1) return interfaces;

        var openings = this._collect_openings(storey);
        if (openings.length) {
            var footprint = this.get_inner_polygon();
            openings.forEach(opening => {
                const op = opening.clone();
                op.project(
                    ceiling.get_point(),
                    [0, 0, 1],
                    [0, 0, 1]
                );
                op.intersects(footprint);
                ceiling.substracts(op);
                if (op.get_area() > 0.01) {
                    let opening_solid = new fh_solid();
                    opening_solid.extrusion(op, [0, 0, roof_bb.size[2] + 0.2]);
                    opening_solid.intersects(thermal_volume);
                    opening_solid.get_faces().forEach(f => {
                        if (Math.abs(f.get_normal()[2]) < 0.01)
                            interfaces.push(f);
                    });
                }
            });
        }
        interfaces.push(ceiling);
        return interfaces;
    }

    //***********************************************************************************
    //**** raytrace
    //***********************************************************************************
    raytrace(origin, direction, max_distance, inner) {
        if (!this._bounding_box.raytrace(origin, direction, max_distance)) return null;
        const normal = cn_normal(direction);
        const plane = cn_dot(origin, normal);
        const diror = cn_dot(direction, origin);
        let max_dist = max_distance;
        let res = null;
        this.space_segments.forEach(sslist => sslist.forEach(ss => {
            //*** transversal acceleration test (can be skipped) */
            const x0 = cn_dot(normal, ss.points[0]) - plane;
            const x1 = cn_dot(normal, ss.points[1]) - plane;
            if (x0 > 0 && x1 > 0) return;
            if (x0 < 0 && x1 < 0) return;

            //*** longitudinal acceleration test (can be skipped)  */
            const y0 = cn_dot(direction, ss.points[0]) - diror;
            const y1 = cn_dot(direction, ss.points[1]) - diror;
            if (y0 > max_dist && y1 > max_dist) return;
            if (y0 < 0 && y1 < 0) return;

            //*** intersection with segment line */
            const pscal = cn_dot(ss.normal, direction);
            if (Math.abs(pscal) < 0.001) return;
            const lambda = cn_dot(ss.normal, cn_sub(ss.points[0], origin)) / pscal;
            if (lambda < 0 || lambda >= max_dist) return;

            //*** intersection with segment */
            const p = cn_add(origin, cn_mul(direction, lambda));
            const x = cn_dot(ss.direction, cn_sub(p, ss.points[0]));
            if (x < 0 || x > ss.size) return;

            //*** Apply result */
            max_dist = lambda;
            res = {
                space: this,
                distance: lambda,
                point: p,
                normal: ss.normal,
                space_segment: ss
            }
        }));
        return res;
    }

    /**
     * Performs a vertex operation
     * @param {function} operation
     */
    vertex_operation(operation) {
        const pg = this.get_inner_polygon();
        const contour_sizes = pg.contour_sizes;
        const contour_vertices = pg.contour_vertices;
        const pg2 = new fh_polygon([0, 0, 0], [0, 0, 1]);
        let offset = 0;
        for (var nct = 0; nct < contour_sizes.length; nct++) {
            const contour2 = [];
            const sz = contour_sizes[nct];
            for (var k = 0; k < sz; k++) {
                const v = cn_clone(contour_vertices[offset + k]);
                operation(v);
                contour2.push(cnx_clone(v));
            }
            pg2.add_contour(contour2);
            offset += sz;
        }
        this.set_inner_polygon(pg2);
    }

    //***********************************************************************************
    //**** find best space center
    //***********************************************************************************
    find_best_center() {
        if (this._center_date >= this.get_date()) return this.center;
        this._center_date = this.get_date();
        var display_res = false;

        var box = new cn_box();
        box.posmin = cn_clone(this.get_bounding_box().posmin);
        box.size = cn_clone(this.get_bounding_box().size);

        //*** first check the center */
        var c = cn_add(box.posmin, cn_mul(box.size, 0.5));
        if (this.contains(c, true)) {
            return c;
        }

        //*** We make a grid on bounding box of approx 1000 samples.
        var area = box.size[0] * box.size[1];
        if (area <= 0) return [0, 0];
        var a = Math.sqrt(area / 1000);

        box.posmin = cn_sub(box.posmin, [2 * a, 2 * a]);
        box.size = cn_add(box.size, [4 * a, 4 * a]);

        var nx = 1 + Math.round(box.size[0] / a);
        var ny = 1 + Math.round(box.size[1] / a);
        var grid = new Array(nx * ny);
        grid.fill(false);

        //*** Draw contours on that grid
        let offset = 0;
        for (var nctr = 0; nctr < this.inner_polygon.contour_sizes.length; nctr++) {
            const sz = this.inner_polygon.contour_sizes[nctr];
            var p0 = this.inner_polygon.contour_vertices[offset + sz - 1];
            for (var v = 0; v < sz; v++) {
                var p1 = this.inner_polygon.contour_vertices[offset + v];
                if (p1[1] == p0[1]) {
                    p0 = p1;
                    continue;
                }
                var pp0, pp1, sense;
                if (p1[1] > p0[1]) {
                    pp0 = p0;
                    pp1 = p1;
                    sense = true;
                } else {
                    pp0 = p1;
                    pp1 = p0;
                    sense = false;
                }

                var j0 = Math.ceil((pp0[1] - box.posmin[1]) / a);
                var j1 = Math.floor((pp1[1] - box.posmin[1]) / a);
                var coef = (pp1[0] - pp0[0]) / (pp1[1] - pp0[1]);
                for (var j = j0; j <= j1; j++) {
                    var y = box.posmin[1] + a * j;
                    var x = pp0[0] + (y - pp0[1]) * coef;
                    var i0 = Math.floor((x - box.posmin[0]) / a);
                    if (!sense) i0++;
                    if (i0 >= 0 && i0 < nx)
                        grid[i0 + j * nx] = !grid[i0 + j * nx];
                }
                p0 = p1;
            }
            offset += sz;
        }

        function console_filter(comment = "", full = false) {
            let txt = "";
            for (var k = 0; k < nx * ny; k++) if (grid[k]) txt += k.toFixed(0);
            console.log(comment, cn_md5(txt));
            if (!full) return;
            for (var j = 0; j < ny; j++) {
                var xx = '';
                for (var i = 0; i < nx; i++) xx += grid[i + j * nx] ? '#' : ' ';
                xx += ' ' + j;
                console.log(xx);
            }
        }

        if (display_res) console_filter("0", true);

        //*** Fill using even odd rule
        for (var j = 0; j < ny; j++) {
            var cnt = 0;
            for (var i = 0; i < nx; i++) if (grid[i + j * nx]) cnt++;
            if (cnt & 1) continue;

            var val = false;
            for (var i = 0; i < nx; i++) {
                if (grid[i + j * nx]) val = !val;
                else grid[i + j * nx] = val;
            }
        }

        if (display_res) console_filter("1");

        //*** Apply median filter until all image is blank
        var sz = 3;
        var threshold = 9;
        var imax = Math.floor(nx / 2);
        var jmax = Math.floor(ny / 2);
        for (var niter = 0; niter < 30; niter++) {
            if (display_res)
                console.log('Iteration  ' + niter + ' seuil ' + threshold);
            var ngrid = new Array(nx * ny);
            ngrid.fill(false);
            var ok = false;
            for (var j = 1; j < ny - 1; j++) {
                for (var i = 1; i < nx - 1; i++) {
                    var n = 0;
                    for (var ki = 0; ki < sz; ki++) {
                        for (var kj = 0; kj < sz; kj++)
                            if (grid[i - 1 + ki + (j - 1 + kj) * nx]) n++;
                    }
                    if (n < threshold) continue;

                    ngrid[i + j * nx] = true;
                    ok = true;
                    imax = i;
                    jmax = j;
                }
            }

            //*** If image is blank, we first try to reduce threshold
            if (!ok) {
                threshold--;
                if (threshold < 5) break;
                continue;
            }

            grid = ngrid;
            if (display_res) console_filter("iter" + niter);
        }

        if (display_res) console_filter("fin");

        return [box.posmin[0] + a * imax, box.posmin[1] + a * jmax];
    }

    /**
     * Update the 3D geometry of floor spacings
     * @param {cn_3d_building} building_3d
     * @param {cn_storey} storey
     */
    update_3d_floor_facings(building_3d, storey) {
        if (this.outside) return;
        const space_id = this.create_id_for_bbp(storey);
        let facing_objects = building_3d.get_3d_objects_by_bim_code(
            CODES_BIM_OUVRAGES.facing_floor
        );
        let obsolete_objects = facing_objects.filter(
            (ob) => ob.json_object.SPACE == space_id
        );

        const zoffset = storey.altitude + this.slab_offset + 0.001;
        const facing_polygons =
            this._facing_floor_polygons.length > 0
                ? this._facing_floor_polygons
                : [this._full_facing_floor_polygon];
        facing_polygons.forEach((facing_pg) => {
            facing_pg.compute_tesselation();

            const new_id = cn_building.create_unique_id(
                storey,
                this,
                'floor_facing',
                facing_pg['facing_trimming']
            );

            //*** Maybe that object already exists ? */
            const existing_object = obsolete_objects.find(
                (ob) => ob.BIMID == new_id
            );
            if (existing_object) {
                logger.log('updating object', new_id);
                //*** In that case, remove from obsolete objects */
                const index = obsolete_objects.indexOf(existing_object);
                obsolete_objects.splice(index, 1);

                //*** Update the object */
                const tess_vertices = facing_pg.tesselation_vertices.map(
                    (v) => [v[0], v[1], v[2] + zoffset]
                );
                const tess_trianges = facing_pg.tesselation_triangles.concat(
                    []
                );
                fh_scene.update_mesh_geometry(
                    existing_object._meshes[0],
                    tess_vertices,
                    tess_trianges
                );
            } else {
                logger.log('new object', new_id);
                //**** Build new object */
                const bbp_facing_floor = this._build_3d_floor_facing(
                    facing_pg,
                    storey,
                    true
                );
                const storey_element = facing_pg['facing_trimming']
                    ? new cn_storey_element(
                        facing_pg['facing_trimming'],
                        storey
                    )
                    : null;
                building_3d.add_element(storey_element, bbp_facing_floor);
            }
        });

        logger.log(
            'remove objects : ',
            obsolete_objects.map((f) => f.BIMID)
        );
        building_3d.remove_objects(obsolete_objects);
    }

    build_3d_floor_facings(storey, cnmap_pointers = false) {
        const bbp_objects = [];
        if (this.outside) return [];
        const facing_polygons =
            this._facing_floor_polygons.length > 0
                ? this._facing_floor_polygons
                : [this._full_facing_floor_polygon];
        facing_polygons.forEach((facing_pg) => {
            bbp_objects.push(
                this._build_3d_floor_facing(facing_pg, storey, cnmap_pointers)
            );
        });
        return bbp_objects;
    }

    _build_3d_floor_facing(facing_pg, storey, cnmap_pointers = false) {
        //**** Build new object */
        const bbp_facing_floor = {};
        bbp_facing_floor.ID = cn_building.create_unique_id(
            storey,
            this,
            'floor_facing',
            facing_pg['facing_trimming']
        );
        bbp_facing_floor.Name = 'Revêtement de sol';
        bbp_facing_floor.Code_BIM = CODES_BIM_OUVRAGES.facing_floor;
        bbp_facing_floor.SPACE = this.create_id_for_bbp(storey);
        bbp_facing_floor.storey = storey.storey_index;
        bbp_facing_floor[CODES_BIM_PARAMETRES_OUVRAGES.facing_surface] =
            facing_pg.get_area();

        bbp_facing_floor.geometries = [];
        const geometry = cn_bbp_geometry.from_polygon(
            facing_pg,
            storey.altitude + this.slab_offset + 0.001
        );
        bbp_facing_floor.geometries.push(geometry);
        const space_facing = facing_pg['facing_trimming']
            ? facing_pg['facing_trimming'].facing
            : this.facings[0];
        geometry.texture = space_facing
            ? cn_image_dir() + 'texture_' + space_facing.texture + '.jpg'
            : '';
        const facing_rgb_color = cn_color_hexa_to_rgb(
            space_facing && space_facing.color ? space_facing.color : '#FFFFFF'
        );
        geometry.color = [...facing_rgb_color, 1];

        if (cnmap_pointers) {
            bbp_facing_floor.cnmap_storey = storey;
            bbp_facing_floor.cnmap_element = facing_pg['facing_trimming']
                ? facing_pg['facing_trimming']
                : this;
            bbp_facing_floor.cnmap_side = 0;
        }
        return bbp_facing_floor;
    }

    /**
     * Builds 3d sub slab facing
     * @param {cn_storey} storey
     * @param {boolean} cnmap_pointers
     * @returns
     */
    build_3d_sub_slab_facing(storey, cnmap_pointers) {
        let ppp = this;
        let obj = this.get_lazy_data(
            () => {
                let polygons = ppp.build_3d_sub_slab_polygons(storey);
                if (polygons.length == 0) return null;

                //**** Build new object */
                const bbp_facing = {};
                bbp_facing.ID = cn_building.create_unique_id(storey, ppp, 'sub_slab_facing');
                bbp_facing.Name = 'Revêtement de dessous de dalle';
                bbp_facing.Code_BIM = CODES_BIM_OUVRAGES.facing_facade;
                bbp_facing.SPACE = ppp.create_id_for_bbp(storey);
                bbp_facing.storey = storey.storey_index;
                bbp_facing.geometries = [];
                let full_area = 0;
                polygons.forEach(pg => {
                    full_area += pg.get_area();

                    let geometry = cn_bbp_geometry.from_polygon(pg);
                    bbp_facing.geometries.push(geometry);

                    const space_facing = ppp.facings[2];
                    geometry.texture = space_facing ? cn_image_dir() + 'texture_' + space_facing.texture + '.jpg' : '';
                    const facing_rgb_color = cn_color_hexa_to_rgb(space_facing && space_facing.color ? space_facing.color : '#FFFFFF');
                    geometry.color = [...facing_rgb_color, 1];
                });
                bbp_facing[CODES_BIM_PARAMETRES_OUVRAGES.facing_surface] = full_area;

                return bbp_facing;
            },
            'sub_slab_facing',
            storey
        );
        if (!cnmap_pointers) return obj;
        let obj1 = {};
        for (var k in obj) obj1[k] = obj[k];
        obj1["cnmap_storey"] = storey;
        obj1["cnmap_element"] = null;
        obj1["cnmap_side"] = 2;
        return obj1;
    }

    /**
     * Internal method to build
     * @param {cn_storey} storey
     * @returns {fh_polygon[]}
     */
    build_3d_sub_slab_polygons(storey) {
        // @ts-ignore
        return this.get_lazy_data(
            () => {
                let polygons = [];
                let storey_below = storey.get_relative_storey(-1);
                if (!storey_below) return polygons;
                let slabs = storey.slabs.filter(slab => slab.spaces[1] == this && (slab.spaces[0] == null || !slab.spaces[0].is_roof(storey_below)));
                if (slabs.length == 0) return polygons;

                slabs.forEach(slab => {
                    let pg = new fh_polygon([0, 0, storey.altitude + this.slab_offset - slab.slab_type.thickness - 0.001], [0, 0, -1])
                    pg.unites(slab.pierced_outer_polygon);
                    polygons.push(pg);
                });
                return polygons;
            },
            'sub_slab_polygons',
            storey
        );
    }

    /**
     * Update the 3D texture of main floor spacing
     * @param {cn_3d_building} building_3d
     * @param {cn_storey} storey
     */
    update_3d_floor_facing_texture(building_3d, storey) {
        if (this.outside) return;
        const id = storey.exterior
            ? cn_building.create_unique_id(storey, this)
            : cn_building.create_unique_id(
                storey,
                this,
                'floor_facing',
                undefined
            );
        const facing_object = building_3d.get_3d_object_by_bimid(id);
        if (facing_object) {
            const space_facing = this.facings[0];
            const texture = space_facing
                ? cn_image_dir() + 'texture_' + space_facing.texture + '.jpg'
                : '';
            const color = [
                ...cn_color_hexa_to_rgb(
                    space_facing && space_facing.color
                        ? space_facing.color
                        : '#FFFFFF'
                ),
                storey.exterior ? 1 : 1,
            ];
            fh_scene.update_mesh_color(
                facing_object._meshes[0],
                color,
                texture
            );
        }
    }

    /**
     * Update the 3D texture of main floor spacing
     * @param {cn_3d_building} building_3d
     * @param {cn_storey} storey
     */
    update_3d_sub_slab_facing_texture(building_3d, storey) {
        if (this.outside) return;
        const id = cn_building.create_unique_id(storey, this, 'sub_slab_facing');
        const facing_object = building_3d.get_3d_object_by_bimid(id);
        if (facing_object) {
            const space_facing = this.facings[2];
            const texture = space_facing ? cn_image_dir() + 'texture_' + space_facing.texture + '.jpg' : '';
            const facing_rgb_color = cn_color_hexa_to_rgb(space_facing && space_facing.color ? space_facing.color : '#FFFFFF');
            const color = [...facing_rgb_color, 1];
            facing_object._meshes.forEach(m => {
                fh_scene.update_mesh_color(m, color, texture);
            });
        }
    }
    reset_properties() {
        this.name = '';
        this.declared_area = -1;
        this.declared_perimeter = -1;
        this.ceiling_height = 2.5;
        this.ceiling_type = null
        this.slab_offset = 0;
        this.space_usage = '';
        this.label_position = [0, 0];
        this.heated = true;
    }

    /**
     * returns ceilings for a given space (lazy calculation)
     * @param {cn_storey} storey
     * @returns {Array<fh_polygon>}
     */
    get_3d_ceilings(storey) {
        // @ts-ignore
        return this.get_lazy_data(
            () => {
                return this._build_3d_ceiling(storey);
            },
            'ceilings',
            storey
        );
    }

    _collect_openings(storey) {

        const building = storey.building;
        var openings = [];
        if (storey.roof) {
            for (var k in storey.roof.openings) {
                var pg_roof_openings = storey.roof.openings[k].build_3d_opening(
                    storey.height
                );
                if (pg_roof_openings) openings.push(pg_roof_openings);
            }
        }

        //*** and upper slab openings */
        var index = building.storeys.indexOf(storey);
        if (index >= 0 && index < building.storeys.length - 1) {
            var upper_storey = building.storeys[index + 1];
            upper_storey.scene.slab_openings.filter(so => !so.slab_type).forEach(so => {
                var pg_slab_openings = so.build_3d_polygon(this.ceiling_height);
                if (pg_slab_openings) openings.push(pg_slab_openings);
            });

        }

        //** remove stairs openings */
        storey.scene.stairs.forEach((st) => {
            const pg = st.build_3d_slab_opening(0);
            if (pg) openings.push(pg);
        });
        return openings;
    }

    /**
     * Build ceilings for a given space
     * @param {cn_storey} storey
     * @returns {Array<fh_polygon>}
     */
    _build_3d_ceiling(storey) {
        const building = storey.building;

        if (!this.is_roof(storey)) return [];

        //*** build space volume */
        const space_volume = this.build_space_volume(storey);
        var ceilings = space_volume
            .get_faces()
            .filter((f) => f.get_normal()[2] > 0.1);

        const thermal_volume_box = this.build_thermal_volume(storey).get_bounding_box();
        const zmax = thermal_volume_box.position[2] + thermal_volume_box.size[2];

        //*** gather all openings from the roof, stairs and slab openings */
        var openings = this._collect_openings(storey);

        //** remove columns */
        storey.scene.columns.forEach((col) => {
            const pg = col.get_footprint();
            if (pg) openings.push(pg);
        });

        let result = [];
        ceilings.forEach((ceiling) => {
            //*** remove openings from all ceilings */
            openings.forEach((opening) => {
                if (ceiling.get_area() > 0.0001) {
                    const op = opening.clone();
                    op.project(
                        ceiling.get_point(),
                        ceiling.get_normal(),
                        [0, 0, 1]
                    );
                    ceiling.substracts(op);
                }
            });
            result.push(ceiling);
        });

        //*** Add sides */
        let interfaces = this.build_ceiling_interfaces(storey).concat([]);
        interfaces.pop();
        result = result.concat(interfaces);

        return result;
    }

    /**
     * Returns IFC base quantities for a space (lazy)
     * @param {cn_storey} storey
     * @returns {object}
     */
    get_metrics(storey) {
        return this.get_lazy_data(
            () => {
                return new cn_space_metrics(this, storey);
            },
            'metrics',
            storey
        );
    }

    compute_facing_count() {
        this.facings.filter(f => !!f).forEach(f => f.count++);
    }
}

